/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useRef, useState} from 'react';

import InformativeText from '../../constants/InformativeText';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import upstreamLeasedAPI from '../../services/modules/AM/upstreamAssetManagement';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {
  getStatusColor,
  hasUserRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  upstreamKey,
  upstreamLabel
} from '../Forms/Constants/IP/UpstreamKeyLabel';
import {emissionUnits} from '../Forms/interface/unitChange';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import Loader from '../Loader/Loader';

import {makeCheckKeyLabel} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import TableTitleHeader from './Utils/TableTitleHeader';

const UpProperty: React.FC<{
  selectedYear: string;
}> = ({selectedYear}) => {
  interface DataType {
    _id: string;
    assetName: string;
    totalSpace: number;
    rentedFrom: string;
    rentedTo: string;
    totalEmission: number;
    cost: number;
    driven: number;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const companyId = useCompanyStore.getState().companyId;
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const {area} = useCompanyStore.getState().preUnits;

  const type = 'property';

  useEffect(() => {
    if (selectedYear) {
      setIsRefresh(true);
      upstreamLeasedAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${type}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      upstreamLeasedAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${type}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    upstreamLeasedAPI(APITypeEnum.DELETE_ALL, {
      upstreamLeasedAsset: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const upPropertyKeyLabel = [
    {
      label: upstreamLabel.LESSOR_NAME,
      key: upstreamKey.LESSOR_NAME,
      type: objTypes.string
    },
    {
      label: upstreamLabel.FLOOR_SPACE,
      key: upstreamKey.FLOOR_SPACE,
      type: objTypes.number,
      unit: area
    },
    {
      label: upstreamLabel.RENTED_FROM,
      key: upstreamKey.RENTED_FROM,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },
    {
      key: upstreamKey.RENTED_TO,
      label: upstreamLabel.RENTED_TO,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },
    {
      label: upstreamLabel.TOTAL_EMISSIONS,
      key: upstreamKey.emissionProperty,
      type: objTypes.number
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = [
    {
      title: upstreamLabel.LESSOR_NAME,
      dataIndex: upstreamKey.LESSOR_NAME,
      ...SearchFilter(
        upstreamLabel.LESSOR_NAME,
        upstreamKey.LESSOR_NAME as keyof DataType
      )
    },

    {
      title: upstreamLabel.FLOOR_SPACE,
      dataIndex: upstreamKey.FLOOR_SPACE,
      //   width: '120px',
      editable: true,
      type: 'number',
      render: (value) => {
        return `${formatIndian(value)} ${area}`;
      }
    },
    {
      title: upstreamLabel.RENTED_FROM,
      dataIndex: upstreamKey.RENTED_FROM,
      editable: true,
      // ...sorter(upstreamKey.RENTED_FROM as keyof DataType),
      // //   width: '100px',
      // ...SearchFilter(
      //   upstreamLabel.RENTED_FROM,
      //   upstreamKey.RENTED_FROM as keyof DataType
      // ),
      render: (value) => moment(value).format('DD-MM-YYYY')
    },
    {
      title: upstreamLabel.RENTED_TO,
      dataIndex: upstreamKey.RENTED_TO,
      editable: true,
      // ...sorter(upstreamKey.RENTED_TO as keyof DataType),
      // //   width: '100px',
      // ...SearchFilter(
      //   upstreamLabel.RENTED_TO,
      //   upstreamKey.RENTED_TO as keyof DataType
      // ),
      render: (value) => moment(value).format('DD-MM-YYYY')
    },
    {
      title: upstreamLabel.TOTAL_EMISSIONS,
      dataIndex: upstreamKey.emissionProperty,
      editable: true,
      // ...sorter(upstreamKey.TOTAL_FUEL_EMISSIONS as keyof DataType),
      //   width: '100px',
      type: 'select',
      // filterer: monthlyYearly,
      // filters: monthlyYearly.map((item) => {
      //   return {text: item.label, value: item.value};
      // }),
      // onFilter: (value, record) => {
      //   return record.totalEmission === value;
      // },
      render: (value) => {
        return `${formatIndian(value)} ${emissionUnits.T_CO2e}`;
      }
    },
    {
      title: makeCheckKeyLabel.STATUS.label,
      // dataIndex: 'status',
      editable: true,
      type: 'select',
      render: (value: any, record: any) => {
        const colorText = getStatusColor(statusEm.ACCEPTED);

        return (
          <Tag
            onClick={() => {
              // setShowModelInfo(true);
              setEditData(record);
              setIsEdit(true);
            }}
            color={colorText.color}
            style={{
              fontWeight: 700
            }}
          >
            {colorText.text}
          </Tag>
        );
      }
    }
    // ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
    //   ? [
    //       {
    //         title: FullInfo.title,
    //         dataIndex: 'FullInfo',
    //         editable: true,
    //         type: 'select',
    //         render: (_: any, record: any) => {
    //           return (
    //             <Button
    //               onClick={() => {
    //                 setShowModelInfo(true);
    //                 setEditData(record);
    //                 setIsEdit(true);
    //               }}
    //               shape="circle"
    //               style={{
    //                 marginRight: 16,
    //                 cursor: 'pointer',
    //                 color: '#435B37',
    //                 background: '#F5FBF3',
    //                 border: 0
    //               }}
    //             >
    //               <ExclamationOutlined />
    //             </Button>
    //           );
    //         }
    //       }
    //     ]
    //   : [])
    // {
    //   title: Title.actionTitle,
    //   dataIndex: 'operation',
    //   type: '',
    //   fixed: 'right',
    //   width: '200px',
    //   render: (value, record) =>
    //     dataSource.length >= 1 ? (
    //       <>
    //         <Popconfirm
    //           title="Sure to delete?"
    //           onConfirm={() => {
    //             handleDelete([record._id]);
    //           }}
    //         >
    //           <a style={{marginRight: '20px'}}>Delete</a>
    //         </Popconfirm>
    //       </>
    //     ) : null
    // }
  ];

  return (
    <div>
      <TableTitleHeader
        title="Upstream Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel: undefined,
          setIsEdit: undefined,
          buttonTitle: ''
        }}
        sampleDownload={{href: ''}}
        jobs={{setShowJobs: null, uploadType: ''}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        onlyUpload={true}
        tooltip={InformativeText.AM.upLeased}
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        // rowSelection={{
        //   type: 'checkbox',
        //   onChange: (selectedKey: React.Key[], selectedRow: DataType[]) => {
        //     setSelectedRowKeys(
        //       selectedRow.map((item) => {
        //         return item._id;
        //       })
        //     );
        //   },
        //   selectedRowKeys
        // }}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={upPropertyKeyLabel} />
        }
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        formPreview={upPropertyKeyLabel}
      />
    </div>
  );
};

export default UpProperty;
